import React, { Fragment, useEffect, useRef } from 'react'

import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import styled from 'styled-components'

import { PrimaryButton } from 'src/Components/Buttons/PrimaryButton'
import { PrimaryInput } from 'src/Components/PrimaryInput'

import { BankDetails } from '../helpers'

import { BankDetailsFormProps } from './props'
import { RenderInput } from './RenderInput'
import { getSchemaBankDetails } from './schema'

import { FormGroup, Wrapper, Form, ButtonWrapper, RowWrapper } from './styles'

const Container = styled.div`
  margin-top: 24px;
`

const BankDetailsForm: React.FunctionComponent<BankDetailsFormProps> = ({
  onSubmit,
  item: { fields },
  handleSpecifyRequirements,
  initialValues,
  serverErrors,
}) => {
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
    clearErrors,
  } = useForm({
    mode: 'all',
    defaultValues: initialValues as Record<string, string>,
    resolver: yupResolver(getSchemaBankDetails(fields)),
  })

  const prevServerErrorKeysRef = useRef<string[]>([])

  useEffect(() => {
    prevServerErrorKeysRef.current.forEach((key) => clearErrors(key))

    const keys = Object.keys(serverErrors)
    keys.forEach((key) =>
      setError(key, { type: 'server', message: serverErrors[key] }),
    )
    prevServerErrorKeysRef.current = keys
  }, [serverErrors, setError, clearErrors])

  const onHandleSubmit = (data: Record<string, string>): void => {
    onSubmit(data as unknown as BankDetails)
  }

  return (
    <Container>
      <Form
        data-testid='bank-details-form'
        onSubmit={handleSubmit(onHandleSubmit)}
      >
        <RowWrapper>
          <Wrapper>
            <FormGroup>
              <Controller
                name='accountHolderName'
                control={control}
                render={({ field }) => (
                  <PrimaryInput
                    {...field}
                    data-testid='account-holder-name'
                    placeholder='Account holder name'
                    error={
                      errors.accountHolderName?.message as string | undefined
                    }
                  />
                )}
              />
            </FormGroup>
          </Wrapper>
          {fields.map(({ group, name }) => (
            <Fragment key={name}>
              {group
                .filter((field) => field.key !== 'accountHolderName')
                .map((field) => (
                  <RenderInput
                    key={field.name}
                    control={control}
                    error={errors}
                    name={
                      field.key === 'email'
                        ? field.name.replace(' (Optional)', '')
                        : field.name
                    }
                    valuesAllowed={field.valuesAllowed}
                    example={field.example}
                    refreshRequirementsOnChange={
                      field.refreshRequirementsOnChange
                    }
                    type={field.type}
                    propKey={field.key}
                    handleSpecifyRequirements={handleSpecifyRequirements}
                  />
                ))}
            </Fragment>
          ))}
        </RowWrapper>
        <ButtonWrapper>
          <PrimaryButton disabled={isSubmitting} type='submit'>
            Save changes
          </PrimaryButton>
        </ButtonWrapper>
      </Form>
    </Container>
  )
}

export default BankDetailsForm
